<script setup lang="ts">
import { ref } from 'vue'

import { useI18n } from '@/app/i18n'

import AppIcon from './AppIcon.vue'
import AppPasswordChecklist from './AppPasswordChecklist.vue'

withDefaults(
  defineProps<{
    placeholder?: string
    invalid?: boolean
    disabled?: boolean
    id?: string
    name?: string
    autocomplete?: string
    /** The four-line rules checklist under the field (register, set password, change password). */
    checklist?: boolean
  }>(),
  {
    placeholder: '',
    invalid: false,
    disabled: false,
    id: undefined,
    name: undefined,
    autocomplete: 'current-password',
    checklist: false,
  },
)

const model = defineModel<string | null | undefined>({ default: '' })
const emit = defineEmits<{ blur: [event: FocusEvent] }>()
const { t } = useI18n()

const visible = ref(false)
</script>

<template>
  <div class="app-password" data-kit="password">
    <div class="app-password__group" :class="{ 'is-invalid': invalid, 'is-disabled': disabled }">
      <input
        :id="id"
        v-model="model"
        :type="visible ? 'text' : 'password'"
        :name="name"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :aria-invalid="invalid || undefined"
        class="app-password__input"
        @blur="emit('blur', $event)"
      />
      <button
        type="button"
        class="app-password__toggle"
        :aria-label="t('common.action.showPassword')"
        :aria-pressed="visible"
        :disabled="disabled"
        data-testid="password-toggle"
        @click="visible = !visible"
      >
        <AppIcon :name="visible ? 'EyeOffIcon' : 'EyeIcon'" :size="14" />
      </button>
    </div>
    <AppPasswordChecklist v-if="checklist" :password="model" />
  </div>
</template>

<style scoped>
.app-password__group {
  display: flex;
  align-items: stretch;
  border: 1px solid var(--p-form-field-border-color);
  border-radius: var(--p-form-field-border-radius);
  background: var(--p-form-field-background);
  transition: border-color 0.2s;
}
.app-password__group:focus-within {
  border-color: var(--p-form-field-focus-border-color);
}
.app-password__group.is-invalid {
  border-color: var(--p-form-field-invalid-border-color);
}
.app-password__group.is-disabled {
  background: var(--p-form-field-disabled-background);
}
.app-password__input {
  flex: 1;
  min-width: 0;
  padding: var(--p-form-field-padding-y) var(--p-form-field-padding-x);
  color: var(--p-form-field-color);
  background: transparent;
  border: 0;
  outline: none;
  font: inherit;
}
.app-password__input::placeholder {
  color: var(--p-form-field-placeholder-color);
}
.app-password__toggle {
  display: inline-flex;
  align-items: center;
  padding: 0 0.857rem;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>
